import { useCallback, useEffect, useRef, useState } from "react";
import {
  JOB_POLL_INTERVAL_MS,
  JOB_TIMEOUT_MS,
  type ChatJobResponse,
} from "@/lib/ai-engine";

/**
 * Tracks chat messages that were queued for the Claude worker.
 *
 * Each queued message becomes a "pending job". While a job is queued/claimed we poll
 * GET /api/conversations/:id/jobs/:jobId every 5s. When the job finishes the caller's
 * `onJobDone` refetches the conversation (the worker has already appended the reply
 * server-side). After 15 minutes without a result the job flips to "timeout" so the UI can
 * show a gentle message with a Retry button.
 *
 * Active jobs are mirrored to localStorage (per `storageKey`) so a page reload keeps
 * polling instead of silently dropping the "Draper is working" state.
 */

export type PendingJobStatus = "queued" | "claimed" | "failed" | "timeout";

export interface PendingChatJob {
  jobId: string;
  conversationId: number | string;
  /** Which thread the job belongs to ("main", "project:3", "conv:12"...) so a view can filter. */
  scope: string;
  /** Original message text, kept so Retry can resend it. */
  messageText: string;
  startedAt: number;
  status: PendingJobStatus;
  error: string | null;
}

export const isActiveJob = (job: PendingChatJob): boolean =>
  job.status === "queued" || job.status === "claimed";

export interface TrackJobInput {
  jobId: string | number;
  conversationId: string | number;
  scope: string;
  messageText: string;
}

interface UseChatJobsOptions {
  /** localStorage key for persistence across reloads; null/undefined disables it. */
  storageKey?: string | null;
  /** Called once when a job reaches "done" (the job is removed from the list afterwards). */
  onJobDone?: (job: PendingChatJob, response: ChatJobResponse) => void;
}

const MAX_PERSISTED_TEXT = 4000;

function loadJobs(storageKey: string | null | undefined): PendingChatJob[] {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter((j): j is PendingChatJob => {
      if (!j || typeof j !== "object") return false;
      const job = j as Partial<PendingChatJob>;
      return (
        typeof job.jobId === "string" &&
        (typeof job.conversationId === "string" || typeof job.conversationId === "number") &&
        typeof job.scope === "string" &&
        typeof job.startedAt === "number" &&
        now - job.startedAt < JOB_TIMEOUT_MS
      );
    }).map((j) => ({ ...j, messageText: String(j.messageText ?? ""), status: "queued" as const, error: null }));
  } catch {
    return [];
  }
}

function saveJobs(storageKey: string | null | undefined, jobs: PendingChatJob[]): void {
  if (!storageKey) return;
  try {
    const active = jobs.filter(isActiveJob);
    if (active.length === 0) localStorage.removeItem(storageKey);
    else localStorage.setItem(storageKey, JSON.stringify(active));
  } catch {
    /* storage unavailable (private mode, quota) — in-memory tracking still works */
  }
}

export function useChatJobs({ storageKey, onJobDone }: UseChatJobsOptions = {}) {
  const [jobs, setJobs] = useState<PendingChatJob[]>([]);
  const jobsRef = useRef<PendingChatJob[]>([]);
  const keyRef = useRef<string | null | undefined>(storageKey);
  const onJobDoneRef = useRef(onJobDone);
  const pollingRef = useRef(false);
  onJobDoneRef.current = onJobDone;

  const commit = useCallback((next: PendingChatJob[], persist = true) => {
    jobsRef.current = next;
    setJobs(next);
    if (persist) saveJobs(keyRef.current, next);
  }, []);

  const patchJob = useCallback((jobId: string, patch: Partial<PendingChatJob>) => {
    if (!jobsRef.current.some((j) => j.jobId === jobId)) return;
    commit(jobsRef.current.map((j) => (j.jobId === jobId ? { ...j, ...patch } : j)));
  }, [commit]);

  const removeJob = useCallback((jobId: string) => {
    if (!jobsRef.current.some((j) => j.jobId === jobId)) return;
    commit(jobsRef.current.filter((j) => j.jobId !== jobId));
  }, [commit]);

  /** Poll every active job once. Safe to call at any time (overlapping calls are ignored). */
  const pollNow = useCallback(async () => {
    if (pollingRef.current) return;
    const active = jobsRef.current.filter(isActiveJob);
    if (active.length === 0) return;
    pollingRef.current = true;
    try {
      await Promise.all(active.map(async (job) => {
        if (Date.now() - job.startedAt > JOB_TIMEOUT_MS) {
          patchJob(job.jobId, { status: "timeout" });
          return;
        }
        try {
          const res = await fetch(
            `/api/conversations/${encodeURIComponent(String(job.conversationId))}/jobs/${encodeURIComponent(job.jobId)}`,
            { credentials: "include" },
          );
          if (res.status === 404) {
            patchJob(job.jobId, { status: "failed", error: "This request is no longer available on the server." });
            return;
          }
          if (!res.ok) return; // transient (5xx, expired session...) — keep polling until the timeout
          const data = (await res.json()) as ChatJobResponse;
          if (data.status === "done") {
            removeJob(job.jobId);
            onJobDoneRef.current?.(job, data);
          } else if (data.status === "failed") {
            patchJob(job.jobId, { status: "failed", error: data.error || "Draper couldn't complete this request." });
          } else if (data.status === "claimed" && job.status !== "claimed") {
            patchJob(job.jobId, { status: "claimed" });
          }
        } catch {
          /* network blip — try again on the next tick */
        }
      }));
    } finally {
      pollingRef.current = false;
    }
  }, [patchJob, removeJob]);

  const trackJob = useCallback((input: TrackJobInput) => {
    const jobId = String(input.jobId);
    if (jobsRef.current.some((j) => j.jobId === jobId)) return;
    commit([
      ...jobsRef.current,
      {
        jobId,
        conversationId: input.conversationId,
        scope: input.scope,
        messageText: input.messageText.slice(0, MAX_PERSISTED_TEXT),
        startedAt: Date.now(),
        status: "queued",
        error: null,
      },
    ]);
  }, [commit]);

  const dismissJob = useCallback((jobId: string) => removeJob(jobId), [removeJob]);

  // Restore persisted jobs (and re-load when the workspace/storage key changes).
  useEffect(() => {
    keyRef.current = storageKey;
    const restored = loadJobs(storageKey);
    commit(restored, false);
    if (restored.length > 0) void pollNow();
  }, [storageKey, commit, pollNow]);

  const activeCount = jobs.filter(isActiveJob).length;
  useEffect(() => {
    if (activeCount === 0) return;
    const id = setInterval(() => { void pollNow(); }, JOB_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeCount, pollNow]);

  return { jobs, trackJob, dismissJob, pollNow };
}
